import { trackFacebookEvent } from '@/shared/features/analytics/utils/facebookCommonAttributes';
import { getCookie } from '@/core/utils/cookieUtils';

export interface FacebookSearchSuggestionProduct {
  id?: number | string;
  productId?: number | string;
  sku?: number | string;
}

export interface FacebookSearchDesign {
  designId: number;
  categoryType?: string;
}

export interface FacebookSearchEventData {
  search_text?: string;
  products?: {
    list?: FacebookSearchSuggestionProduct[];
  };
  designs?: FacebookSearchDesign[];
  result_count?: number;
  zero_results?: boolean;
  sort_order?: string;
}

let fbSearchDebounce: ReturnType<typeof setTimeout> | undefined;

/**
 * Meta Pixel - Search Event
 *
 * Triggers the Meta "Search" standard event when a user performs a meaningful
 * search and search results are available.
 */
export function facebookSearchEvent(data?: FacebookSearchEventData): void {
  const triggerEvent = (source: 'suggestion' | 'page') => {
    const searchType = typeof window !== 'undefined' && window.location.pathname.includes('/search/image') ? 'Image' : 'Text';

    const searchString =
      new URLSearchParams(window.location.search).get('search_query') ||
      data?.search_text;

    if (searchType !== 'Image' && !searchString) {
      return;
    }

    let contentIds: string[] = [];
    let contentCategories: string[] = [];

    if (data?.products?.list?.length) {
      contentIds = data.products.list
        .slice(0, 9)
        .map((item) => item.id ?? item.productId ?? item.sku)
        .filter((id) => id != null && id !== '')
        .map(String);
    } else if (data?.designs?.length) {
      contentIds = data.designs
        .slice(0, 9)
        .map((item) => item.designId)
        .filter(Boolean)
        .map(String);
      contentCategories = data.designs
        .slice(0, 9)
        .map((item) => item.categoryType)
        .filter((category): category is string => Boolean(category));
    } else {
      const productCards = document.querySelectorAll<HTMLElement>(
        '#search-result li:not(.valentine-wdg)',
      );
      contentIds = [...productCards]
        .slice(0, 9)
        .map((card) => card.dataset.pid)
        .filter((id): id is string => Boolean(id));
      contentCategories = [...productCards]
        .slice(0, 9)
        .map((card) => card.dataset.cat)
        .filter((category): category is string => Boolean(category));
    }

    const resultCount = data?.result_count ?? (data?.products?.list?.length || contentIds.length);
    const zeroResults = data?.zero_results ?? (resultCount === 0);
    const resolvedSortOrder = data?.sort_order || getCookie('sortingPreference');

    const payload = {
      content_type: 'product',
      content_ids: contentIds,
      content_category: contentCategories.join(', '),
      source,
      search_type: searchType,
      result_count: resultCount,
      zero_results: zeroResults,
      ...(searchType !== 'Image' && searchString ? { search_string: searchString } : {}),
      ...(resolvedSortOrder ? { sort_order: resolvedSortOrder } : {}),
    };

    trackFacebookEvent('Search', payload);
  };

  if (data?.products?.list?.length) {
    clearTimeout(fbSearchDebounce);
    fbSearchDebounce = setTimeout(() => {
      triggerEvent('suggestion');
    }, 1000);
    return;
  }

  triggerEvent('page');
}
